<template>
  <div data-page-name="analyst-report">
    <div class="wrapper">
      <AppBreadcrumbs :items="[post?.data?.title]" append accent="light" />

      <h1 v-if="post?.data?.data?.post_title">
        {{ post?.data?.data?.post_title }}
      </h1>

      <h1 v-else>
        {{ post?.data?.title }}
      </h1>

      <h2 v-if="post?.data?.data?.post_subtitle">
        {{ post?.data?.data?.post_subtitle }}
      </h2>

      <Buttons.Link
        v-if="post?.data?.data?.pdf_attachment_link"
        :to="post.data.data.pdf_attachment_link"
        target="_blank"
      >
        {{ $t('Download_PDF') }}
        <SvgIcon name="download" />
      </Buttons.Link>

      <article>
        <div
          v-if="
            post?.data?.data?.video_url && post?.data?.data?.video_preview_url
          "
          class="video"
        >
          <NuxtImg
            :src="post.data.data.video_preview_url"
            width="100%"
            height="auto"
          />

          <Buttons.Link
            v-if="post?.data?.data?.video_url"
            :to="post.data.data.video_url"
          >
            <SvgIcon name="play-arrow" />
          </Buttons.Link>
        </div>

        <div
          v-if="post?.data?.content"
          v-html="post.data.content"
          v-photo-swipe="{
            caption: isTouchDevice
              ? $t('ArticleMarkup.zoom-caption-pinch')
              : $t('ArticleMarkup.zoom-caption-scroll', { icon: SvgZoomIn }),
          }"
        />
      </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { showError } from '#app';
import Buttons from '~/components/redesign/Buttons';
import AppBreadcrumbs from '~/components/redesign/AppBreadcrumbs.vue';

// directives
import vPhotoSwipe from '~/directives/photoswipe';

// icon
import SvgZoomIn from '~/assets/svg/zoom-in.svg?raw';

// utils
import get from 'lodash.get';

// types
import { PostStatus } from '~/types';

const {
  params: { slug },
  query: { preview },
} = useRoute();

const { error, data: post } = await useAsyncGql({
  operation: 'AnalystReportsArticle',
  variables: {
    slug: slug as string,
    status: preview ? undefined : PostStatus.PUBLISHED,
  },
  options: {
    transform: data => data.blog_article,
  },
});

if (error.value || !post.value) {
  showError(`Page not found: ${slug}`);
}

const isTouchDevice = computed(
  () => import.meta.client && 'ontouchstart' in document,
);

useSeoMeta({
  title: get(post.value, 'data.seo.title'),
  ogTitle: get(post.value, 'data.seo.title'),
  description: get(post.value, 'data.seo.description'),
  ogDescription: get(post.value, 'data.seo.description'),
  ogImage: get(post.value, 'data.seo.image'),
  ogType: get(post.value, 'data.seo.og_type'),
});
</script>

<style lang="scss" scoped>
@use '_/fn';
@use '_/mixins/size';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/typo';
@use '_/variables/font';

[data-page-name='analyst-report'] {
  padding: 4rem 2rem 8.25rem;

  @include media.tablet {
    padding: 1.5rem 2rem 5rem;
  }

  @include media.mobile {
    padding: 0 1rem 2.5rem;
  }

  .wrapper {
    @include size.wrapper(76rem);

    [data-component-name='AppBreadcrumbs'] {
      margin-bottom: 5rem;

      @include media.tablet {
        margin-bottom: 3rem;
      }

      @include media.mobile {
        padding: 1rem 1rem 0.5rem;
        margin: 0 -1rem 1rem;
      }
    }

    h1 {
      @include typo.h3;

      margin-bottom: 0.5rem;
    }

    h2 {
      @include typo.h4;

      padding: 0.5rem 0;
    }

    :deep([data-component-name='Buttons.Link']) {
      display: block;
      width: max-content;
      margin: 1.5rem 0;

      @include media.tablet {
        margin-bottom: 1.5rem;
      }

      @include media.mobile {
        width: 100%;
      }

      svg {
        fill: fn.token('icon-reversed');
      }
    }

    .video {
      @include size.fluid-max-width(74%);

      position: relative;
      display: block;
      margin: 2rem 0;

      @include media.tablet {
        @include size.fluid-max-width(100%);
      }

      a {
        position: absolute;
        margin: 0;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        @include media.mobile {
          max-width: fit-content;
        }
      }
    }

    :deep(article) {
      h2,
      h3,
      p,
      table,
      ul {
        @include size.fluid-max-width(74%);

        @include media.tablet {
          @include size.fluid-max-width(100%);
        }
      }

      figure.image {
        @include size.fluid-max-width(100%);

        overflow-x: auto;
        margin: 0 0 2rem;
      }

      img {
        @include media.mobile {
          width: 100%;
          height: auto;
        }
      }

      h2,
      h3 {
        @include typo.subtitle-1;

        margin-bottom: 2rem;
      }

      h4 {
        @include typo.subtitle-3;

        margin-bottom: 1rem;
      }

      p {
        margin-bottom: 2rem;

        @include typo.body-2;

        a {
          color: fn.token('link');

          @include typo.link-text-2;

          &:hover {
            background-color: rgb(fn.token('link'), 16%);
          }
        }
      }

      p.caption {
        @include typo.caption;

        margin-top: -0.625rem;
      }

      ul {
        margin-left: 1rem;
        margin-bottom: 1rem;

        li {
          @include typo.body-2;

          position: relative;
          margin-bottom: 1rem;

          &::before {
            all: unset;
            content: '';
            display: block;

            @include size.fixed(4.5px);

            background-color: fn.token('text-primary');
            border-radius: 50%;
            position: absolute;
            top: 12px;
            left: -12px;
          }
        }
      }

      figure.table {
        width: 100%;
        margin-left: 0;
        margin-right: 0;

        table {
          border-spacing: 0;
          border: 0;
          width: 100%;
          height: auto;
          max-height: max-content;
          font-family: font.$inter;
          font-size: 1.125rem;

          th,
          td {
            box-sizing: border-box;
            padding: 1rem 0;
            text-align: center;
          }

          thead {
            color: fn.token('text-primary');

            @include typo.subtitle-3;

            tr {
              th {
                border-bottom: 2px solid fn.token('outline-primary');
              }
            }
          }

          tbody {
            th,
            td {
              @include typo.body-3;

              border-bottom: 1px solid fn.token('outline-secondary');
            }
          }
        }

        figcaption {
          @include typo.body-3;

          padding-top: 0.5rem;
        }
      }
    }
  }
}
</style>
